import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, User, ChevronDown, Check } from 'lucide-react';
import { useCalculator } from '../calculator/hooks/useCalculator.ts';
import { useForexCalculator } from '../forex/hooks/useForexCalculator.ts';
import { useStocksCalculator } from '../stocks/hooks/useStocksCalculator.ts';
import { useFuturesCalculator } from '../futures/hooks/useFuturesCalculator.ts';
import PocketCrypto from './PocketCrypto';
import PocketForex from './PocketForex';
import PocketStocks from './PocketStocks';
import PocketFutures from './PocketFutures';
import PocketSettings from './PocketSettings';
import './pocket.css';

const MARKETS = ['Crypto', 'Forex', 'Stocks', 'Futures'];
const VIEWS = { Crypto: PocketCrypto, Forex: PocketForex, Stocks: PocketStocks, Futures: PocketFutures };

// The Pocket (compact text) calculator shell — used in the app build. Title bar
// (window controls · QUER · settings · account), market tabs, then the active
// market's view. All four market hooks are owned here so the Settings sheet can
// edit the same live state the views render.
const PocketCalc = () => {
  const [market, setMarket] = useState('Crypto');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // any copy (value/sub/fee/input) dispatches a 'pk-copied' window event → flash a toast
  useEffect(() => {
    let t;
    const onCopied = () => { setCopied(true); clearTimeout(t); t = setTimeout(() => setCopied(false), 1100); };
    window.addEventListener('pk-copied', onCopied);
    return () => { window.removeEventListener('pk-copied', onCopied); clearTimeout(t); };
  }, []);

  const crypto = useCalculator();
  const forex = useForexCalculator();
  const stocks = useStocksCalculator();
  const futures = useFuturesCalculator();
  const hooks = { Crypto: crypto, Forex: forex, Stocks: stocks, Futures: futures };

  // crypto/forex/stocks updateValues accept an updater fn (and replace, so we
  // spread prev); futures updateValues merges a partial → use updateValue.
  const mergeSet = (hook) => (key, val) => hook.setValues((prev) => ({ ...prev, [key]: val }));
  const setFieldFor = {
    Crypto: mergeSet(crypto),
    Forex: mergeSet(forex),
    Stocks: mergeSet(stocks),
    Futures: (key, val) => futures.updateValue(key, val),
  };

  const user = (typeof window !== 'undefined' && window.Clerk && window.Clerk.user) || null;
  const userName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Account' : 'Account';
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const signOut = () => { try { window.Clerk?.signOut?.(); } catch {} };

  return (
    <div className="pk-root min-h-screen bg-[#05070b] flex justify-center text-[#cdd4de]">
      <div className="relative w-full max-w-[340px] min-h-screen bg-[#0d1117] flex flex-col border-x border-white/5">
        {/* title bar — QUER centred (macOS draws the real window controls via hiddenInset);
            market selector + Settings + Account on the right.
            WebkitAppRegion 'drag' makes the bar move the frameless Electron window;
            the button group is 'no-drag' so its clicks register (inert on the web). */}
        <div className="relative flex items-center px-3 py-2.5 border-b border-white/5" style={{ WebkitAppRegion: 'drag' }}>
          <div className="ml-auto flex items-center gap-1 z-10" style={{ WebkitAppRegion: 'no-drag' }}>
            {/* market selector — shows the current market, click → pick another */}
            <div className="relative">
              <button onClick={() => { setAcctOpen(false); setSettingsOpen(false); setMarketOpen((v) => !v); }}
                className="flex items-center gap-1 h-6 pl-2 pr-1.5 rounded-md text-[11px] font-medium text-[#cdd4de] hover:bg-white/[0.06] transition-colors" title="Market">
                {market}<ChevronDown size={11} className="text-[#6b7480]" />
              </button>
              {marketOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMarketOpen(false)} />
                  <div className="absolute right-0 top-7 z-40 bg-[#11161f] border border-white/10 rounded-xl shadow-xl p-1 min-w-[130px]" onClick={(e) => e.stopPropagation()}>
                    {MARKETS.map((m) => (
                      <button key={m} onClick={() => { setMarket(m); setMarketOpen(false); }}
                        className={`block w-full text-left text-[12.5px] px-2.5 py-1.5 rounded-md transition-colors ${m === market ? 'text-white bg-white/[0.06]' : 'text-[#cdd4de] hover:bg-white/[0.04]'}`}>{m}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => { setAcctOpen(false); setMarketOpen(false); setSettingsOpen((v) => !v); }} className="w-6 h-6 flex items-center justify-center rounded-md text-[#6b7480] hover:text-[#cdd4de] hover:bg-white/[0.06]" title="Settings"><SettingsIcon size={15} /></button>
            <button onClick={() => { setSettingsOpen(false); setMarketOpen(false); setAcctOpen((v) => !v); }} className="w-6 h-6 flex items-center justify-center rounded-md text-[#6b7480] hover:text-[#cdd4de] hover:bg-white/[0.06]" title="Account"><User size={15} /></button>
          </div>
        </div>

        {/* body — each market stays mounted so its inputs persist across switches */}
        <div className="pk-scroll flex-1 overflow-y-auto px-2.5 pt-2.5 pb-4 pl-4">
          {MARKETS.map((m) => {
            const View = VIEWS[m];
            return (
              <div key={m} className={m === market ? '' : 'hidden'}>
                <View mode="full" hook={hooks[m]} onOpenSettings={() => setSettingsOpen(true)} />
              </div>
            );
          })}
        </div>

        {/* settings sheet */}
        {settingsOpen && (
          <PocketSettings
            market={market}
            values={hooks[market].values}
            metrics={hooks[market].metrics}
            setField={setFieldFor[market]}
            onClose={() => setSettingsOpen(false)}
          />
        )}

        {/* account menu */}
        {acctOpen && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setAcctOpen(false)} />
            <div className="absolute right-2 top-11 z-40 bg-[#11161f] border border-white/10 rounded-xl shadow-xl p-1.5 min-w-[170px]" onClick={(e) => e.stopPropagation()}>
              <div className="px-2 pb-2 pt-1 border-b border-white/5 mb-1">
                <div className="text-[13px] text-[#e4e9ef] font-medium">{userName}</div>
                {userEmail && <div className="text-[11px] text-[#6b7480]">{userEmail}</div>}
              </div>
              {['Account', 'Manage subscription', 'Billing'].map((x) => (
                <button key={x} className="block w-full text-left text-[12.5px] text-[#cdd4de] px-2 py-1.5 rounded-md hover:bg-white/[0.06]">{x}</button>
              ))}
              <button onClick={signOut} className="block w-full text-left text-[12.5px] text-[#f2767f] px-2 py-1.5 rounded-md hover:bg-white/[0.06]">Sign out</button>
            </div>
          </>
        )}

        {/* copy confirmation — fires on ANY copy via the window 'pk-copied' event */}
        <div className={`pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-200 ${copied ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#11161f] border border-white/10 text-[#cdd4de] text-[11px] font-medium shadow-xl">
            <Check size={12} className="text-[#54cca0]" /> Copied
          </span>
        </div>
      </div>
    </div>
  );
};

export default PocketCalc;
